import React, { Dispatch, useCallback, useContext, useEffect, useMemo } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { ApprovalResult } from '../generated-types';
import { AnalyticsEvents, trackEvent } from '../services/analytics';
import { checkPayment } from '../services/paymentService';
import { initialState } from './signingInitialState';
import signingReducer from './signingReducer';
import { Action, Actions, SigningFlowState, SigningAction, SigningTypes } from './signingTypes';

const SigningContext = React.createContext<SigningTypes | undefined>(undefined);

const retryStates = ['PROCESSING', 'EXPIRED'];

export function useSigningContext<T = void>(action?: (args: T) => Promise<ApprovalResult>) {
    const context = useContext(SigningContext)!;
    if (context === undefined) {
        throw new Error('useSigningContext must be used within a SigningContext');
    }
    const { state, dispatch, analyticsCategory, analyticsEvent } = context;

    const showPaymentRetry = useMemo(
        () => state.signingStatus && retryStates.includes(state.signingStatus),
        [state.signingStatus],
    );

    const trackSigningEvent = useCallback(
        (action: string, label?: string) => trackEvent(analyticsEvent, analyticsCategory, action, label),
        [analyticsEvent, analyticsCategory],
    );

    const trackSigningEventWithOverwrite = useCallback(
        (overwriteAnalyticsCategory?: string) => (action: string, label?: string) =>
            trackEvent(analyticsEvent, overwriteAnalyticsCategory ?? analyticsCategory, action, label),
        [analyticsEvent, analyticsCategory],
    );

    const startSigning = useCallback(
        (args: T, overwriteAnalyticsCategory?: string) => {
            dispatch({ type: Actions.SIGN_START });
            trackSigningEventWithOverwrite(overwriteAnalyticsCategory)('bankid', 'start');
            action?.(args)
                .then((result: ApprovalResult) => dispatch({ type: Actions.SIGN_SUCCESS, result }))
                .catch((error) => dispatch({ type: Actions.SIGN_ERROR, error }));
        },
        [dispatch, trackSigningEventWithOverwrite, action],
    );

    const handleCancelSigningFlow = useCallback(
        (overwriteAnalyticsCategory?: string) => {
            dispatch({ type: Actions.RESET });
            trackSigningEventWithOverwrite(overwriteAnalyticsCategory)('bankid', 'cancel');
        },
        [trackSigningEventWithOverwrite, dispatch],
    );

    const signingCompleted =
        (state.signingStatus === 'COMPLETED' ||
            state.signingStatus === 'REJECTED' ||
            state.signingStatus === 'BIM_EXPIRED') &&
        !state.signingOperationPending &&
        !state.showSigningFlow;

    return {
        ...state,
        dispatch,
        startSigning,
        handleCancelSigningFlow,
        showPaymentRetry,
        trackSigningEvent,
        trackSigningEventWithOverwrite,
        signingCompleted,
    };
}

const usePaymentCheck = (state: SigningFlowState, dispatch: Dispatch<Action>) => {
    const shouldCheckPayment =
        state.signingStatus === 'CANCELLED' ||
        (state.signingStatus === 'UNKNOWN' && state.dynamicLinkId && !state.signingOperationPending) ||
        !!(state.signingStatus === 'REJECTED' && state.dynamicLinkId);

    const checkPaymentStatus = useCallback(async () => {
        try {
            const status = await checkPayment(state.dynamicLinkId!);
            dispatch({ type: Actions.PAYMENT_CHECKED, status });
        } catch (error) {
            dispatch({ type: Actions.SIGN_ERROR, error: error as RequestExceptionWithResponse });
        }
    }, [dispatch, state.dynamicLinkId]);

    useEffect(() => {
        if (shouldCheckPayment) {
            checkPaymentStatus();
        }
    }, [checkPaymentStatus, shouldCheckPayment]);
};

export function SigningContextProvider({
    children,
    analyticsCategory,
    analyticsEvent = AnalyticsEvents.HOME,
}: SigningAction) {
    const [state, dispatch] = React.useReducer(signingReducer, initialState);
    usePaymentCheck(state, dispatch);

    const contextValue = useMemo(
        () => ({
            state,
            dispatch,
            analyticsCategory,
            analyticsEvent,
        }),
        [state, dispatch, analyticsCategory, analyticsEvent],
    );

    return <SigningContext.Provider value={contextValue}>{children}</SigningContext.Provider>;
}
